import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SimplePeer from 'simple-peer';
import { useAuthStore } from '../store/authStore';
import { useSocketStore } from '../store/socketStore';
import { useTranslate } from '../i18n';
import { API_URL } from '../config';
import { ensureCallPermissions } from '../native/permissions';
import { setActiveCall } from '../notifyFocus';
import { FiMic, FiMicOff, FiVideo, FiVideoOff, FiPhoneOff } from 'react-icons/fi';

export default function CallPage() {
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const t = useTranslate();
  const { token } = useAuthStore();
  const { socket, subscribeSignals, subscribeReady, subscribeAccepted } = useSocketStore();

  const peerUserId = searchParams.get('peer');
  const role = searchParams.get('role') || 'initiator';
  const callType = searchParams.get('type') || 'video';
  const isVideo = callType !== 'audio';
  const isInitiator = role === 'initiator';

  const [ended, setEnded] = useState(false);
  const [status, setStatus] = useState(isInitiator ? t('calling') : t('connecting'));
  const [localStream, setLocalStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(isVideo);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const iceRef = useRef(null);
  const pendingRef = useRef([]);
  const startedMediaRef = useRef(false);
  const peerStartedRef = useRef(false);
  const calleeReadyRef = useRef(!isInitiator);
  const acceptedRef = useRef(!isInitiator);
  const remoteAttachedRef = useRef(false);
  const endedRef = useRef(false);

  useEffect(() => {
    setActiveCall(callId);
    return () => setActiveCall(null);
  }, [callId]);

  const attachRemote = (remoteStream) => {
    if (!remoteStream) return;
    remoteAttachedRef.current = true;
    setStatus(t('inCall'));
    const el = remoteVideoRef.current;
    if (el) {
      el.srcObject = remoteStream;
      el.play?.().catch(() => {});
    }
  };

  const flushSignals = () => {
    const queued = pendingRef.current;
    pendingRef.current = [];
    queued.forEach((signal) => {
      try {
        peerRef.current?.signal(signal);
      } catch (error) {
        console.error('Failed to apply queued signal', error);
      }
    });
  };

  const enqueueSignal = (signal) => {
    if (!signal) return;
    if (peerRef.current) {
      try {
        peerRef.current.signal(signal);
      } catch (error) {
        pendingRef.current.push(signal);
      }
    } else {
      pendingRef.current.push(signal);
    }
  };

  const fetchIceServers = async () => {
    try {
      const response = await axios.get(`${API_URL}/calls/ice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      iceRef.current = response.data.iceServers;
      return iceRef.current;
    } catch (error) {
      console.error('Failed to load ICE servers', error);
      iceRef.current = [{ urls: ['stun:stun.cloudflare.com:3478'] }];
      return iceRef.current;
    }
  };

  const destroyPeer = () => {
    try {
      peerRef.current?.destroy();
    } catch (error) {
      // ignore
    }
    peerRef.current = null;
    peerStartedRef.current = false;
  };

  const createPeer = async (initiator, policy = 'all') => {
    if (peerStartedRef.current || !streamRef.current) return;
    peerStartedRef.current = true;
    setStatus(t('connecting'));
    const iceServers = iceRef.current || (await fetchIceServers());
    const peer = new SimplePeer({
      initiator,
      trickle: true,
      stream: streamRef.current,
      config: { iceServers, iceTransportPolicy: policy }
    });

    peer.on('signal', (data) => {
      socket?.emit('webrtc-signal', { to: peerUserId, signal: data });
    });
    peer.on('stream', attachRemote);
    peer.on('connect', () => {
      if (!remoteAttachedRef.current) setStatus(t('inCall'));
    });
    peer.on('error', (err) => {
      console.error('Peer error:', err);
      if (!remoteAttachedRef.current) setStatus(t('error'));
    });
    peer.on('close', () => {
      if (!endedRef.current && !remoteAttachedRef.current) {
        setStatus(t('connecting'));
      }
    });

    peerRef.current = peer;
    flushSignals();
  };

  const maybeStartInitiator = () => {
    if (!isInitiator) return;
    if (acceptedRef.current && calleeReadyRef.current) {
      createPeer(true);
    }
  };

  useEffect(() => {
    if (!socket || !peerUserId || startedMediaRef.current) return undefined;
    startedMediaRef.current = true;

    (async () => {
      try {
        await ensureCallPermissions();
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: isVideo ? { width: 640, height: 480, facingMode: 'user' } : false
        });
        streamRef.current = stream;
        setLocalStream(stream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        await fetchIceServers();
        if (!isInitiator) {
          await createPeer(false);
          socket.emit('webrtc-ready', { to: peerUserId, callId });
        } else {
          setStatus(t('waitingForPeer'));
          maybeStartInitiator();
        }
      } catch (error) {
        console.error('Failed to start call:', error);
        const denied =
          error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError';
        setStatus(denied ? t('cameraDenied') : t('error'));
      }
    })();

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, callId, peerUserId]);

  useEffect(() => {
    return () => {
      endedRef.current = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      destroyPeer();
    };
  }, []);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    const offSignal = subscribeSignals((data) => {
      if (data.from && peerUserId && data.from !== peerUserId) return;
      enqueueSignal(data.signal);
    });
    const offReady = subscribeReady((data) => {
      if (data.from && peerUserId && data.from !== peerUserId) return;
      calleeReadyRef.current = true;
      maybeStartInitiator();
    });
    const offAccepted = subscribeAccepted(() => {
      acceptedRef.current = true;
      setStatus(t('connecting'));
      maybeStartInitiator();
    });

    return () => {
      offSignal();
      offReady();
      offAccepted();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerUserId, callId, t, subscribeSignals, subscribeReady, subscribeAccepted]);

  useEffect(() => {
    if (!socket) return undefined;

    const onStatus = (data) => {
      if (data.callId && data.callId !== callId) return;
      if (data.status === 'rejected' || data.status === 'ended') {
        endedRef.current = true;
        streamRef.current?.getTracks().forEach((track) => track.stop());
        destroyPeer();
        setEnded(true);
      }
    };

    socket.on('call-status', onStatus);
    return () => socket.off('call-status', onStatus);
  }, [socket, callId]);

  const handleEndCall = async () => {
    endedRef.current = true;
    try {
      await axios.put(
        `${API_URL}/calls/${callId}`,
        { status: 'ended', endedAt: new Date().toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to update call status', error);
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    destroyPeer();
    setEnded(true);
  };

  const toggleAudio = () => {
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
    });
    setIsMuted((value) => !value);
  };

  const toggleVideo = () => {
    streamRef.current?.getVideoTracks().forEach((track) => {
      track.enabled = !track.enabled;
    });
    setIsVideoEnabled((value) => !value);
  };

  if (ended) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-canvas text-ink p-6">
        <div className="nest-card p-8 w-full max-w-sm text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-nest" aria-hidden="true" />
          <h2 className="text-2xl font-bold mb-6">{t('endCall')}</h2>
          <button
            onClick={() => navigate('/chat')}
            className="min-h-[48px] w-full rounded-2xl bg-primary text-white px-8 font-semibold cursor-pointer hover:opacity-90 transition"
          >
            {t('chat')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="call-container relative">
      <p className="absolute top-4 inset-x-0 z-10 text-center text-sm text-ink font-medium pt-[env(safe-area-inset-top)]">
        {status}
      </p>
      <div className="video-grid">
        <div className="relative min-h-[50vh] md:min-h-0 rounded-[1.75rem] overflow-hidden bg-ink shadow-card">
          {isVideo ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-nest text-ink">
              {t('startCall')}
            </div>
          )}
          <span className="absolute top-3 start-3 text-xs text-white bg-ink/50 px-3 py-1 rounded-full">
            {t('remoteUser')}
          </span>
        </div>
        <div className="relative h-40 md:h-auto rounded-[1.75rem] overflow-hidden bg-ink shadow-card md:order-none order-first">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          <span className="absolute top-3 start-3 text-xs text-white bg-ink/50 px-3 py-1 rounded-full">
            {t('online')}
          </span>
        </div>
      </div>

      <div className="fixed bottom-6 inset-x-0 z-20 flex justify-center pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-3 bg-white rounded-full shadow-nav px-3 py-2">
          <button
            onClick={toggleAudio}
            className={`h-14 w-14 rounded-full inline-flex items-center justify-center cursor-pointer transition ${
              isMuted ? 'bg-red-500 text-white' : 'bg-canvas text-ink hover:bg-nest'
            }`}
            aria-label={isMuted ? t('unmute') : t('mute')}
          >
            {isMuted ? <FiMicOff size={22} /> : <FiMic size={22} />}
          </button>
          {isVideo && (
            <button
              onClick={toggleVideo}
              className={`h-14 w-14 rounded-full inline-flex items-center justify-center cursor-pointer transition ${
                !isVideoEnabled ? 'bg-red-500 text-white' : 'bg-canvas text-ink hover:bg-nest'
              }`}
              aria-label={t('startVideo')}
            >
              {!isVideoEnabled ? <FiVideoOff size={22} /> : <FiVideo size={22} />}
            </button>
          )}
          <button
            onClick={handleEndCall}
            className="h-14 w-14 rounded-full bg-red-500 text-white inline-flex items-center justify-center cursor-pointer hover:bg-red-400 transition"
            aria-label={t('endCall')}
          >
            <FiPhoneOff size={22} />
          </button>
        </div>
      </div>
    </div>
  );
}
